import { Trash2 } from 'lucide-react'
import React, { useState } from 'react'

import { FileList, FileUpload, IconButton, } from 'slab'

export interface ReimbursementFileUploadProps {
  files: File[]
  handleFilesChange: (files: File[]) => void
  accept?: string
  multiple?: boolean
  maxFiles?: number
  supportedFormats?: string[]
  maxFileSize?: number // in MB
}

const formattedFileName = (fileName: string) => {
  return fileName.length > 50
    ? `${fileName.substring(0, 47)}...`
    : fileName
}

const ReimbursementFileUpload = ({
  files,
  handleFilesChange,
  accept = 'image/png, image/jpeg, image/heic, application/pdf',
  multiple = false,
  maxFiles = 5,
  supportedFormats = ['PNG', 'JPG', 'HEIC', 'PDF'],
  maxFileSize = 10, // in MB
}: ReimbursementFileUploadProps) => {
  const [error, setError] = useState<string | null>(null)

  const validateFiles = (newFiles: File[]): { validFiles: File[], errors: string[] } => {
    const validFiles: File[] = []
    const errors: string[] = []

    // Check total number of files
    if (newFiles.length + files.length > maxFiles) {
      errors.push(`We can only take up to ${maxFiles} files at a time`)
      return { validFiles, errors }
    }

    // Process each file
    for (const file of newFiles) {
      const { isValid, errorMessage } = validateFile(file)

      if (isValid) {
        validFiles.push(file)
      } else if (errorMessage) {
        errors.push(errorMessage)
      }
    }

    return { validFiles, errors }
  }

  const validateFile = (file: File): { isValid: boolean, errorMessage: string } => {
    // Check if file was already uploaded first
    if (files.some(existingFile => existingFile.name === file.name && existingFile.size === file.size)) {
      return {
        isValid: false,
        errorMessage: `${file.name} was already added - we kept the earlier version to be safe`
      }
    }

    // Check if file is empty
    if (file.size === 0) {
      return {
        isValid: false,
        errorMessage: `${file.name} is empty (0 bytes). Please select a valid file`
      }
    }

    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return {
        isValid: false,
        errorMessage: `${file.name} is too large. Files need to be ${maxFileSize}MB or less`
      }
    }

    // Check file type
    const fileType = file.type.toLowerCase()
    const isValidType = accept.split(',').some(type => {
      const trimmedType = type.trim().toLowerCase()
      if (trimmedType.includes('*')) {
        const baseType = trimmedType.split('/')[0]
        return fileType.startsWith(baseType)
      }
      return fileType === trimmedType
    })

    if (!isValidType) {
      return {
        isValid: false,
        errorMessage: `${file.name} is not in a supported format. Supported formats: (${supportedFormats.join(', ')})`
      }
    }

    return { isValid: true, errorMessage: '' }
  }

  const handleFileChange = (newFiles: File[]) => {
    const { validFiles, errors } = validateFiles(newFiles)

    if (validFiles.length > 0) {
      handleFilesChange([...files, ...validFiles])
    }

    if (errors.length > 0) {
      setError(errors.join('\n'))
    } else {
      setError(null)
    }
  }

  const handleRemove = (index: number) => {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    handleFilesChange(newFiles)
    setError(null)
  }

  return (
    <div className='space-y-4'>
      <FileUpload
        accept={accept}
        multiple={multiple}
        error={error}
        id='file-upload'
        data-testid='file-upload'
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length > 0) {
            handleFileChange(Array.from(e.target.files))
            // Reset the input value so the same file can trigger the onChange event again
            e.target.value = ''
          }
        }}
      >
        <FileUpload.Content details={
          <ul className='space-y-1 text-center'>
            <li>Supported formats: {supportedFormats.join(', ')}</li>
            <li>Maximum file size: {maxFileSize}MB</li>
          </ul>
          }
        />
      </FileUpload>

      <div className='space-y-2'>
        <div className='flex items-center space-x-2'>
          <p className='label-default font-medium text-ds-text-secondary'>
            {!files.length && 'No file uploaded'}
            {files.length >= 1 && `${files.length} file${files.length === 1 ? '' : 's'} uploaded`}
          </p>
        </div>

        {files.length > 0 && (
          <FileList>
            {files.map((file, index) => (
              <FileList.File
                key={`${file.name}-${index}`}
                name={formattedFileName(file.name)}
                size={file.size}
                status='success'
                actions={
                  <IconButton
                    onClick={() => { handleRemove(index) }}
                    label='Remove file'
                    icon={Trash2}
                  />
              }
              />
            ))}
          </FileList>
        )}
      </div>
    </div>
  )
}

export default ReimbursementFileUpload
